import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";
import { ApiEnvelope, HomeSummaryDTO } from "../models/HomeSummaryDTO";

/** Thrown for any non-2xx response or malformed envelope. */
export class HomeApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data: unknown = null
  ) {
    super(message);
    this.name = "HomeApiError";
  }
}

/** Base path for every v1 Home endpoint. */
const HOME_API_PATH = `${API_BASE_URL}/api/v1/home`;

/**
 * HTTP access for the v1 Home dashboard.
 *
 * Follows the same fetch + bearer-token pattern as the other feature data
 * sources in this codebase (see `features/reco/data/datasources`), so auth and
 * error handling behave identically.
 */
export class HomeApiDataSource {
  private getAuthHeaders(): HeadersInit {
    const token = getToken();

    if (!token) {
      throw new HomeApiError("No authentication token found", 401);
    }

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * GET /api/v1/home/summary
   *
   * @param signal Lets the caller cancel in-flight work on unmount.
   */
  async fetchSummary(signal?: AbortSignal): Promise<HomeSummaryDTO> {
    const response = await fetch(`${HOME_API_PATH}/summary`, {
      method: "GET",
      headers: this.getAuthHeaders(),
      signal,
    });

    let body: ApiEnvelope<HomeSummaryDTO> | null = null;

    try {
      body = await response.json();
    } catch (error) {
      // Fall through to the status check below; a non-JSON body from an error
      // response is more useful reported as an HTTP failure than a parse error.
      body = null;
    }

    if (!response.ok) {
      throw new HomeApiError(
        body?.message || `Request failed with status ${response.status}`,
        response.status,
        body
      );
    }

    if (!body?.success || !body?.data) {
      throw new HomeApiError(
        body?.message || "Malformed home summary response",
        response.status,
        body
      );
    }

    return body.data;
  }
}
